/**
 * VAPI call subtitles. Not a child of #own-vapi-menu — that menu is only the Y anchor.
 * Same live/final merge as VRVapiTranscriptionWithCSS3D.
 * Each speaker turn is one line. A speaker change starts a new line.
 * The last three turns stay visible: bottom opacity 1, middle 0.5, top 0.2.
 */
export class OwnVapiMenuSubtitles {
  constructor({ model, getAgentId, menuSelector = '#own-vapi-menu' }) {
    this.model = model;
    this.getAgentId = getAgentId;
    this.menuSelector = menuSelector;
    this._subtitleHistory = [];
    this._liveSubtitleEntry = null;
    this._trackedAgentId = null;
    this._followRaf = 0;
    this.root = this._createRoot();
    document.body.appendChild(this.root);
    this._setupTranscriptListeners();
  }

  _createRoot() {
    const el = document.createElement('div');
    el.id = 'own-vapi-subtitles';
    el.className = 'own-vapi-subtitles';
    el.setAttribute('aria-live', 'polite');
    el.hidden = true;
    return el;
  }

  _menuElement() {
    return document.querySelector(this.menuSelector);
  }

  _isMenuVisible() {
    const menu = this._menuElement();
    return !!menu && menu.getClientRects().length > 0;
  }

  /** Keep the block sitting on the menu's top edge while that menu is on screen. */
  followMenu() {
    if (this._followRaf) return;
    const tick = () => {
      if (!this._isMenuVisible()) {
        this.root.hidden = true;
        this._followRaf = 0;
        return;
      }
      this._placeAboveMenu();
      this._followRaf = requestAnimationFrame(tick);
    };
    this._followRaf = requestAnimationFrame(tick);
  }

  _placeAboveMenu() {
    const menu = this._menuElement();
    if (!menu || !this.root.childElementCount) {
      this.root.hidden = true;
      return;
    }
    const rect = menu.getBoundingClientRect();
    const margin = 16;
    const viewport = window.innerWidth;
    const isDesktop = viewport > 992;
    if (isDesktop) {
      const width = Math.min(600, Math.max(0, viewport - margin * 2));
      this.root.style.left = '50%';
      this.root.style.right = 'auto';
      this.root.style.width = `${width}px`;
      this.root.style.maxWidth = `${width}px`;
      this.root.style.transform = 'translate(-50%, -100%)';
    } else {
      const width = Math.max(0, viewport - margin * 2);
      this.root.style.left = `${margin}px`;
      this.root.style.right = 'auto';
      this.root.style.width = `${width}px`;
      this.root.style.maxWidth = 'none';
      this.root.style.transform = 'translateY(-100%)';
    }
    this.root.style.top = `${rect.top - 8}px`;
    this.root.hidden = false;
  }

  _eventAgentId(event) {
    const id = event?.data?.vapiAgentId;
    return id == null ? null : id;
  }

  _isForActiveAgent(event) {
    const eventId = this._eventAgentId(event);
    if (eventId == null) return false;
    const activeId = this.getAgentId?.() ?? null;
    const trackedId = activeId != null ? activeId : this._trackedAgentId;
    if (trackedId == null) return false;
    return String(eventId) === String(trackedId);
  }

  _rememberAgent(event) {
    const id = this._eventAgentId(event);
    if (id != null) this._trackedAgentId = id;
  }

  _setupTranscriptListeners() {
    if (!this.model) return;
    this._handleTranscript = (event) => {
      if (!this._isForActiveAgent(event)) return;
      this._rememberAgent(event);
      const data = event?.data || {};
      const role = data.role || 'agent';
      const text = data.transcript || '';
      if (!text) return;
      if (!data.isFinal) {
        this._setLiveSubtitle(role, text);
        return;
      }
      this._clearLiveSubtitle(role);
      this._addSubtitle(role, text, { sentAt: data.sentAt, forceNewEntry: data.source === 'command' });
    };
    this._handleCallStart = (event) => {
      if (!this._isForActiveAgent(event)) return;
      this._rememberAgent(event);
      this._resetSubtitles();
    };
    this._handleCallEnd = (event) => {
      if (!this._isForActiveAgent(event)) return;
      this._resetSubtitles();
      this._trackedAgentId = null;
    };
    this._handleAgentChanged = (event) => {
      if (!this._isForActiveAgent(event)) return;
      this._resetSubtitles();
    };
    this.model.addEventListener('vapi-agent-transcript', this._handleTranscript);
    this.model.addEventListener('vapi-agent-call-start', this._handleCallStart);
    this.model.addEventListener('vapi-agent-call-end', this._handleCallEnd);
    this.model.addEventListener('vapi-agent-changed', this._handleAgentChanged);
  }

  _normalizeTranscriptText(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/[\W_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  _mergeSubtitleText(prev, next) {
    if (!prev) return next;
    const prevTrimmed = prev.replace(/[\s.,;:!?]+$/, '');
    if (next.startsWith(prev) || (prevTrimmed && next.startsWith(prevTrimmed))) return next;
    const prefixLen = Math.min(prev.length, next.length);
    let commonPrefix = 0;
    for (let i = 0; i < prefixLen && prev[i] === next[i]; i++) commonPrefix++;
    if (commonPrefix >= 6 && next.length >= commonPrefix) return next;
    let overlap = Math.min(prev.length, next.length);
    while (overlap > 0) {
      if (prev.endsWith(next.slice(0, overlap))) return prev + next.slice(overlap);
      overlap--;
    }
    return prev + (/[\s.:,;!?]$/.test(prev) ? '' : ' ') + next;
  }

  _reconcileSubtitleText(prev, next) {
    if (!prev) return next;
    const normalizedPrev = this._normalizeTranscriptText(prev);
    const normalizedNext = this._normalizeTranscriptText(next);
    if (!normalizedNext) return prev;
    if (!normalizedPrev) return next;
    if (
      normalizedNext === normalizedPrev ||
      normalizedNext.startsWith(normalizedPrev) ||
      normalizedPrev.startsWith(normalizedNext)
    ) {
      return next;
    }
    return this._mergeSubtitleText(prev, next);
  }

  _setLiveSubtitle(role, text) {
    const transcript = (text || '').trim();
    if (!transcript) return;
    const normalizedRole = role === 'user' ? 'user' : 'assistant';
    this._liveSubtitleEntry = { role: normalizedRole, text: transcript };
    this._renderSubtitleHistory();
  }

  _clearLiveSubtitle(role = null) {
    if (!this._liveSubtitleEntry) return;
    if (role != null) {
      const normalizedRole = role === 'user' ? 'user' : 'assistant';
      if (this._liveSubtitleEntry.role !== normalizedRole) return;
    }
    this._liveSubtitleEntry = null;
    this._renderSubtitleHistory();
  }

  _addSubtitle(role, text, opts = {}) {
    const transcript = (text || '').trim();
    if (!transcript) return;
    const normalizedRole = role === 'user' ? 'user' : 'assistant';
    const sentAt = Number.isFinite(Number(opts.sentAt)) ? Number(opts.sentAt) : Date.now();
    const forceNewEntry = !!opts.forceNewEntry;
    const hadLiveForRole = !!(this._liveSubtitleEntry && this._liveSubtitleEntry.role === normalizedRole);
    if (hadLiveForRole) this._liveSubtitleEntry = null;
    const history = this._subtitleHistory;
    const last = history[history.length - 1];
    if (!forceNewEntry && last && last.role === normalizedRole) {
      const reconciled = this._reconcileSubtitleText(last.text, transcript);
      if (reconciled === last.text) {
        if (hadLiveForRole) this._renderSubtitleHistory();
        return;
      }
      last.text = reconciled;
    } else {
      history.push({ role: normalizedRole, text: transcript, sentAt });
    }
    this._renderSubtitleHistory();
  }

  _getRoleLabel(role) {
    const isUser = role === 'user';
    if (typeof $trans === 'undefined') return isUser ? 'You' : 'Agent';
    if (isUser) return $trans('you-text') || 'You';
    return $trans('vrai-agent-text') || 'Agent';
  }

  _resetSubtitles() {
    this._subtitleHistory = [];
    this._liveSubtitleEntry = null;
    this._renderSubtitleHistory();
  }

  _oneLine(text) {
    return String(text || '').replace(/\s+/g, ' ').trim();
  }

  /** Last three speaker turns. The current speaker stays on one line until the other speaks. */
  _visibleLines() {
    const entries = this._subtitleHistory.map((entry) => ({ role: entry.role, text: entry.text }));
    const live = this._liveSubtitleEntry;
    if (live) {
      const last = entries[entries.length - 1];
      if (last && last.role === live.role) {
        last.text = this._reconcileSubtitleText(last.text, live.text);
      } else {
        entries.push({ role: live.role, text: live.text });
      }
    }
    return entries
      .map((entry) => ({ role: entry.role, text: this._oneLine(entry.text) }))
      .filter((entry) => entry.text)
      .slice(-3);
  }

  _renderSubtitleHistory() {
    const el = this.root;
    if (!el) return;
    const lines = this._visibleLines();
    el.replaceChildren();
    if (!lines.length) {
      el.hidden = true;
      return;
    }
    const opacities = [0.2, 0.5, 1];
    const offset = opacities.length - lines.length;
    lines.forEach((entry, index) => {
      const isUser = entry.role === 'user';
      const line = document.createElement('div');
      line.className = `own-vapi-subtitles__line ${isUser ? 'is-user' : 'is-assistant'}`;
      line.style.opacity = String(opacities[offset + index]);
      const role = document.createElement('span');
      role.className = 'own-vapi-subtitles__role';
      role.style.color = isUser ? '#7dd3fc' : '#00d4aa';
      role.textContent = `${this._getRoleLabel(entry.role)}:`;
      const text = document.createElement('span');
      text.className = 'own-vapi-subtitles__text';
      const textInner = document.createElement('span');
      textInner.className = 'own-vapi-subtitles__text-inner';
      textInner.textContent = entry.text;
      text.appendChild(textInner);
      line.append(role, text);
      el.appendChild(line);
    });
    this.followMenu();
  }
}
